import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { TrendingUp, TrendingDown, AlertTriangle, DollarSign, Calendar as CalendarIcon } from 'lucide-react';
import { budgetData, salesMetrics } from '../../data/mockData';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';

const Header = () => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
  };

  const getPresetRange = (preset) => {
    const today = new Date();
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    let start = new Date(end);

    if (preset === 'last_7') {
      start.setDate(end.getDate() - 6);
    } else if (preset === 'last_30') {
      start.setDate(end.getDate() - 29);
    } else if (preset === 'this_month') {
      start = new Date(end.getFullYear(), end.getMonth(), 1);
    } else if (preset === 'last_month') {
      const firstOfThisMonth = new Date(end.getFullYear(), end.getMonth(), 1);
      const lastMonthEnd = new Date(firstOfThisMonth.getTime() - 24 * 60 * 60 * 1000);
      start = new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1);
      return { from: start, to: lastMonthEnd };
    }
    return { from: start, to: end };
  };

  const [preset, setPreset] = React.useState('last_30');
  const [dateRange, setDateRange] = React.useState(() => getPresetRange('last_30'));
  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);

  const handlePresetChange = (value) => {
    setPreset(value);
    const range = getPresetRange(value);
    setDateRange(range);
  };

  const rangeLabel = dateRange?.from && dateRange?.to
    ? `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
    : 'Select date range';

  const getVarianceBadge = (variance) => {
    if (Math.abs(variance) > 10) {
      return variance > 0 ? 
        <Badge className="bg-red-100 text-red-800 hover:bg-red-200">Overspend {variance.toFixed(1)}%</Badge> :
        <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200">Underspend {Math.abs(variance).toFixed(1)}%</Badge>;
    }
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-200">On Track {variance.toFixed(1)}%</Badge>;
  };

  return (
    <div className="mb-8">
      <div className="mb-6">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
          Media Mix Modelling Dashboard
        </h1>
        <p className="text-gray-600 text-lg">Executive Summary - Media Audit & Optimization Platform</p>
        <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-10 px-4">
                <CalendarIcon className="h-4 w-4 mr-2" />
                {rangeLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <div className="p-3">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  selected={dateRange}
                  onSelect={(range) => {
                    setDateRange(range || { from: null, to: null });
                  }}
                  initialFocus
                />
                <div className="flex justify-end gap-2 pt-3">
                  <Button variant="ghost" size="sm" onClick={() => setIsCalendarOpen(false)}>Cancel</Button>
                  <Button size="sm" onClick={() => setIsCalendarOpen(false)}>Apply</Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <div className="min-w-[220px]">
            <Select value={preset} onValueChange={handlePresetChange}>
              <SelectTrigger aria-label="Date range preset">
                <SelectValue placeholder="Select preset" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last_7">Last 7 days</SelectItem>
                <SelectItem value="last_30">Last 30 days</SelectItem>
                <SelectItem value="this_month">This month</SelectItem>
                <SelectItem value="last_month">Last month</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-purple-500 shadow-lg hover:shadow-xl transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Total Budget</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(budgetData.planned)}</p>
              </div>
              <div className="h-12 w-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500 shadow-lg hover:shadow-xl transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Actual Spend</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(budgetData.actual)}</p>
                <div className="flex items-center mt-1">
                  {budgetData.actual < budgetData.planned ? 
                    <TrendingDown className="h-4 w-4 text-green-500 mr-1" /> :
                    <TrendingUp className="h-4 w-4 text-red-500 mr-1" />
                  }
                  <span className={`text-sm ${
                    budgetData.actual < budgetData.planned ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(Math.abs(budgetData.actual - budgetData.planned))} 
                    {budgetData.actual < budgetData.planned ? 'under' : 'over'}
                  </span>
                </div>
              </div>
              <div className="h-12 w-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500 shadow-lg hover:shadow-xl transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Sales Growth</p>
                <p className="text-2xl font-bold text-green-600">{salesMetrics.totalSalesGrowth}%</p>
                <p className="text-sm text-gray-500 mt-1">YTD Performance</p>
              </div>
              <div className="h-12 w-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500 shadow-lg hover:shadow-xl transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">MTD Variance</p>
                <div className="flex items-center">
                  {getVarianceBadge(budgetData.mtd.variance)}
                </div>
                <p className="text-sm text-gray-500 mt-1">{formatCurrency(budgetData.mtd.actual)} spent</p>
              </div>
              <div className="h-12 w-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Header;